import type { PersistedRedemptionFlowState } from "@/interfaces/redemption";

/**
 * sessionStorage persistence for the recipient redemption flow (see PersistedRedemptionFlowState).
 * Keyed by the gift token so two gift links opened in the same tab never collide. Every read/write
 * is best-effort: private-mode or disabled storage must never break the flow, so failures are
 * swallowed and simply mean "no resume". sessionStorage (not localStorage) is deliberate — the
 * stored draft carries shipping PII and this is a public page, so it must die with the tab and is
 * cleared outright when the redemption completes.
 */
const KEY_PREFIX = "gfg_redemption_flow:";
const keyFor = (token: string) => `${KEY_PREFIX}${token}`;

export function loadRedemptionFlowState(token: string): PersistedRedemptionFlowState | null {
  if (typeof window === "undefined" || !token) return null;
  try {
    const raw = window.sessionStorage.getItem(keyFor(token));
    return raw ? (JSON.parse(raw) as PersistedRedemptionFlowState) : null;
  } catch {
    return null;
  }
}

export function saveRedemptionFlowState(token: string, state: PersistedRedemptionFlowState): void {
  if (typeof window === "undefined" || !token) return;
  try {
    window.sessionStorage.setItem(keyFor(token), JSON.stringify(state));
  } catch {
    // Storage full or unavailable (private mode) — persistence is best-effort.
  }
}

export function clearRedemptionFlowState(token: string): void {
  if (typeof window === "undefined" || !token) return;
  try {
    window.sessionStorage.removeItem(keyFor(token));
  } catch {
    // ignore
  }
}
